import { FormattedMessage } from 'react-intl';
import { Box, Flex } from '@theme-ui/components';

import Loader from '@app/components/Loader';
import { H2 } from '@app/components/typography';
import PricingPageHeader from '@app/pages/Pricing/components/PricingPageHeader/PricingPageHeader';
import useShopProduct from '@app/pages/Pricing/hook/useShopProduct';
import styles from '@app/pages/ProductFinder/ProductFinder.styles';
import { rem } from '@app/styles/theme/layout';
import { SortDirection } from '@__graphql__/types';

import { useCustomerProductUpgradeQuery } from './__graphql__/WhatsNewForMe';
import Authenticated from './Authenticated';
import Guest from './Guest';
import messages from './WhatsNewForMe.messages';

export const CURRENT_PAGE = 1;
export const PAGE_SIZE = 16;

type ContentProps = {
  sku: string;
  title: string;
  includedProductsUrl: string;
};

const Content = ({ sku, title, includedProductsUrl }: ContentProps) => {
  const { loading: magentoProductLoading, magentoProduct } =
    useShopProduct(sku);

  const {
    data: productUpgradeData,
    loading: productUpgradeLoading,
    fetchMore: productUpgradeFetchMore,
    error: productUpgradeError,
  } = useCustomerProductUpgradeQuery({
    variables: {
      sku: sku ?? '',
      currentPage: CURRENT_PAGE,
      pageSize: PAGE_SIZE,
      sortOrders: [
        {
          field: 'price',
          direction: SortDirection.Desc,
        },
      ],
    },
    ssr: false,
    notifyOnNetworkStatusChange: true,
  });

  if (productUpgradeError) {
    throw new Error(
      `Unable to get product upgrade data for sku ${sku}: ${productUpgradeError}`,
    );
  }

  const isAuthenticated =
    productUpgradeData?.userV2?.__typename === 'AuthenticatedUser';

  return (
    <Box>
      <Box mb={5} data-testid="whats-new-for-me-header">
        <PricingPageHeader sku={sku} showPrices />
      </Box>

      <Box css={styles.wrapper} data-testid="whats-new-for-me-title">
        <Box mb={rem(48)} sx={{ textAlign: 'center' }}>
          <H2 as="h1">
            <FormattedMessage {...messages.title} />
          </H2>
        </Box>

        {productUpgradeLoading && magentoProductLoading && (
          <Flex sx={{ justifyContent: 'center' }}>
            <Loader />
          </Flex>
        )}

        {!productUpgradeLoading && !isAuthenticated && <Guest />}

        {isAuthenticated && productUpgradeData && magentoProduct && (
          <Authenticated
            title={title}
            includedProductsUrl={includedProductsUrl}
            productUpgradeData={productUpgradeData}
            magentoProduct={magentoProduct}
            fetchMore={productUpgradeFetchMore}
            isLoading={productUpgradeLoading}
          />
        )}
      </Box>
    </Box>
  );
};

export default Content;
